import React from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import moment from 'moment'
import { useTranslation } from 'react-i18next' // Adjust import as needed
import { invoiceStatus, IPayable } from '@linqpal/models/src/dictionaries'
import { EInvoiceType, InvoicePaymentType } from '@linqpal/models'
import { InvoiceListItem } from '../../../../ui/atoms/InvoiceListItem'
import { getDisplayAmount } from '../../../../utils/helpers/getDisplayAmount'
import { EmptyInvoicePageMessage } from './components'
import { dispatcher, pathFactory, paths } from '../../../links'

interface Props {
  invoices: IPayable[]
  navigation: any
  route: any
  isFactoring: boolean
}

const InvoicesListDisplay = ({
  invoices,
  navigation,
  route,
  isFactoring,
}: Props) => {
  const { t } = useTranslation('global')

  const canViewInvoiceDetails = [
    invoiceStatus.pastDue,
    invoiceStatus.due,
    invoiceStatus.placed,
    invoiceStatus.paymentFailed,
    invoiceStatus.applicationCancelled,
    invoiceStatus.applicationRejected,
    invoiceStatus.pendingDisbursement,
    invoiceStatus.creditApplied,
    invoiceStatus.applicationProcessing,
  ]

  const paidStatuses = [
    invoiceStatus.paid,
    invoiceStatus.collected,
    invoiceStatus.creditApplied,
  ]

  const InvoiceItemOnPress = (invoice: IPayable) => {
    if (
      invoice.paymentType === InvoicePaymentType.FACTORING &&
      invoice.type === EInvoiceType.INVOICE
    ) {
      const action = dispatcher(
        pathFactory('Console.Payables.PaymentHistory'),
        {
          id: invoice._id,
        },
      )
      navigation.replace(action.name, action.params)
    } else if (paidStatuses.includes(invoice.invoiceStatus)) {
      const action = dispatcher(pathFactory('Console.Payables.Receipt'), {
        id: invoice._id,
      })
      navigation.replace(action.name, action.params)
    } else if (
      canViewInvoiceDetails.includes(invoice.invoiceStatus) &&
      !invoice.supplierInvitationDetails?.paymentMethodId && // To check if payment was initiated for an uploaded invoice
      !invoice.totalProcessingAmount
    ) {
      navigation.navigate(paths.Console.Payables.Invoice, {
        id: invoice._id,
      })
    }
  }

  const formatInvoiceDate = (invoice: IPayable) => {
    if (invoice.type === EInvoiceType.QUOTE) {
      if (invoice.authorization_deadline) {
        const date = moment(invoice.authorization_deadline)
          .tz('America/Chicago')
          .format('MMM D')
        return t('Receivables.expires-on', { date })
      } else return ''
    } else {
      const date = moment(invoice.invoice_due_date).format('MMM D')
      return t('Receivables.due-on', { date })
    }
  }

  const renderItem = ({ item }: { item: IPayable }) => {
    return (
      <InvoiceListItem
        key={item._id}
        onPress={() => InvoiceItemOnPress(item)}
        title={
          item.supplierInvitationDetails
            ? item.supplierInvitationDetails?.name
            : item.company?.name
        }
        subtitle={formatInvoiceDate(item)}
        status={item.invoiceStatus}
        type={item.type}
        isFactoring={isFactoring}
        amount={getDisplayAmount(item.total_amount)}
        style={{ marginBottom: 10, marginHorizontal: 0 }}
      />
    )
  }

  return (
    <FlatList
      data={invoices}
      renderItem={renderItem}
      keyExtractor={(item) => item._id}
      contentContainerStyle={styles.container}
      initialNumToRender={15}
      maxToRenderPerBatch={10}
      windowSize={20}
      ListEmptyComponent={
        <View style={styles.emptyResult}>
          <EmptyInvoicePageMessage />
        </View>
      }
    />
  )
}

const styles = StyleSheet.create({
  container: {},
  item: {
    marginBottom: 10,
  },
  emptyResult: {
    justifyContent: 'center',
    marginBottom: 60,
  },
})

export default InvoicesListDisplay
